/**
 * Loads the Microsoft Advertising **UET tag** (`bat.js`) on the client. The
 * checkout `purchase` event (see `useAnalytics`) is configured as a Microsoft
 * Ads **conversion goal**, with Enhanced Conversions via the hashed `pid`
 * (email/phone) we set before the purchase. The same `purchase` is re-sent
 * server-side (UET Conversions API) keyed on the same `transactionId`, so a
 * blocked browser still converts; Microsoft dedups the two.
 *
 * `msclkid` (the Microsoft click id) is captured into the shared
 * `.masterdaweb.com` attribution cookie by `marketing-attribution.client.ts`.
 *
 * Complete no-op (no script injected) when `uetTagId` is empty.
 */
declare global {
  interface Window {
    // UET's queue starts as an array and is replaced by a UET instance whose
    // `.push()` keeps the same call signature, so an array type is sufficient.
    uetq: unknown[];
    UET?: new (opts: Record<string, unknown>) => unknown;
  }
}

export default defineNuxtPlugin(() => {
  const tagId = useRuntimeConfig().public.uetTagId as string;
  if (!tagId) return;

  // Official UET bootstrap (queue first; the lib swaps it for a UET instance
  // on load and fires the initial pageLoad). enableAutoSpaTracking covers
  // Nuxt SPA route changes.
  window.uetq = window.uetq || [];
  const init = () => {
    const opts = { ti: tagId, enableAutoSpaTracking: true } as Record<
      string,
      unknown
    >;
    opts.q = window.uetq;
    if (window.UET) {
      window.uetq = new window.UET(opts) as unknown as unknown[];
      window.uetq.push('pageLoad');
    }
  };

  const src = '//bat.bing.com/bat.js';
  if (!document.querySelector(`script[src="${src}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.onload = init;
    document.head.appendChild(script);
  } else {
    init();
  }
});
